/**
 * Jobs module — centralized, explicit job classification + customer/display
 * resolution. Phase 1 of the Operations → Jobs refinement.
 *
 * SCHEMA-SAFE: reads existing task fields only. Writes nothing. Adds no
 * stored statuses or address snapshots. Shared so Jobs stays consistent with
 * how Dispatch/Today already resolve the customer (clientName-first).
 *
 * Lifecycle bucket is decided by STATUS FIRST, then by the presence of
 * `scheduledStart` only where it genuinely matters (scheduled vs unscheduled).
 */
import { getDisplayName } from './utils';

// Canonical lifecycle buckets used for grouping + filtering in the Jobs page.
export const JOB_BUCKETS = {
  UNSCHEDULED: 'unscheduled',
  SCHEDULED: 'scheduled',
  IN_PROGRESS: 'in_progress', // active-in-field: in_progress + en_route + on_site
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  UNKNOWN: 'unknown',
};

// Statuses that mean "a tech is actively engaged" — these must NEVER be
// hidden from Active by a date filter, and always land in the IN_PROGRESS bucket.
const FIELD_ACTIVE_STATUSES = new Set(['in_progress', 'en_route', 'on_site']);

// Per-status badge presentation. Keys are normalized (lowercased) statuses.
// Anything not present here is treated as a truly unknown status.
const STATUS_BADGE = {
  unscheduled: { label: 'Unscheduled', cls: 'bg-gray-100 text-gray-600',   color: '#9CA3AF' },
  approved:    { label: 'Approved',    cls: 'bg-purple-100 text-purple-700', color: '#A855F7' },
  scheduled:   { label: 'Scheduled',   cls: 'bg-blue-100 text-blue-700',    color: '#3B82F6' },
  en_route:    { label: 'En Route',    cls: 'bg-cyan-100 text-cyan-700',    color: '#06B6D4' },
  on_site:     { label: 'On Site',     cls: 'bg-teal-100 text-teal-700',    color: '#14B8A6' },
  in_progress: { label: 'In Progress', cls: 'bg-amber-100 text-amber-700',  color: '#EAB308' },
  completed:   { label: 'Completed',   cls: 'bg-green-100 text-green-700',  color: '#22C55E' },
  cancelled:   { label: 'Cancelled',   cls: 'bg-rose-100 text-rose-700',    color: '#F43F5E' },
  unknown:     { label: 'Unknown Status', cls: 'bg-gray-100 text-gray-500', color: '#6B7280' },
};

const norm = (s) => (typeof s === 'string' ? s.trim().toLowerCase() : '');
const hasDate = (task) => Boolean(task && task.scheduledStart);

/**
 * Classify a job task into an explicit lifecycle bucket + badge.
 * Status-first; scheduledStart only disambiguates scheduled vs unscheduled
 * and rescues completed-without-date from being mislabelled.
 *
 * @returns {{ bucket:string, status:string, isFieldActive:boolean,
 *             badgeLabel:string, badgeCls:string, color:string }}
 */
export function classifyJob(task) {
  const status = norm(task && task.status);

  // 1) Terminal states win outright, regardless of date.
  if (status === 'cancelled') {
    return _out(JOB_BUCKETS.CANCELLED, status, false, 'cancelled');
  }
  if (status === 'completed') {
    // Completed without a date must NOT be labelled Unscheduled.
    return _out(JOB_BUCKETS.COMPLETED, status, false, 'completed');
  }

  // 2) Field-active states are ALWAYS active (date is irrelevant here).
  if (FIELD_ACTIVE_STATUSES.has(status)) {
    return _out(JOB_BUCKETS.IN_PROGRESS, status, true, status);
  }

  // 3) No date → needs scheduling. Preserve an Approved badge distinction.
  if (!hasDate(task)) {
    if (status === 'approved') {
      return _out(JOB_BUCKETS.UNSCHEDULED, status, false, 'approved');
    }
    // scheduled/pending/empty without a date all read as Unscheduled here.
    if (status === '' || status === 'scheduled' || status === 'pending' || status === 'unscheduled') {
      return _out(JOB_BUCKETS.UNSCHEDULED, status || 'unscheduled', false, 'unscheduled');
    }
    // A recognized-but-odd status with no date → keep discoverable as unknown.
    return _out(JOB_BUCKETS.UNKNOWN, status, false, 'unknown');
  }

  // 4) Has a date. pending is normalized to scheduled upstream, but guard here too.
  if (status === '' || status === 'scheduled' || status === 'pending') {
    return _out(JOB_BUCKETS.SCHEDULED, status || 'scheduled', false, 'scheduled');
  }
  if (status === 'approved') {
    // Approved WITH a date behaves like scheduled operationally, badge stays Approved.
    return _out(JOB_BUCKETS.SCHEDULED, status, false, 'approved');
  }

  // 5) Anything else with a date is a truly unknown status — never silently
  //    show as Scheduled; keep it discoverable via All Jobs.
  return _out(JOB_BUCKETS.UNKNOWN, status, false, 'unknown');
}

function _out(bucket, status, isFieldActive, badgeKey) {
  const b = STATUS_BADGE[badgeKey] || STATUS_BADGE.unknown;
  return {
    bucket,
    status,
    isFieldActive,
    badgeLabel: b.label,
    badgeCls: b.cls,
    color: b.color,
  };
}

/**
 * Does this job pass the selected date filter?
 * Field-active jobs (in_progress/en_route/on_site) ALWAYS pass so active work
 * is never hidden. Unscheduled/cancelled/unknown-without-date have no date to
 * test and always pass. Scheduled & completed are filtered by scheduledStart.
 *
 * @param {object} task
 * @param {'all'|'today'|'week'} dateFilter
 * @param {Date} [now]
 */
export function passesDateFilter(task, dateFilter, now = new Date()) {
  if (dateFilter === 'all') return true;
  const { isFieldActive } = classifyJob(task);
  if (isFieldActive) return true;            // never hide active field work
  if (!hasDate(task)) return true;           // nothing to test against

  const d = new Date(task.scheduledStart);
  if (isNaN(d.getTime())) return true;

  const today = new Date(now); today.setHours(0, 0, 0, 0);
  const todayEnd = new Date(today); todayEnd.setHours(23, 59, 59, 999);
  const weekEnd = new Date(today); weekEnd.setDate(weekEnd.getDate() + 7);

  if (dateFilter === 'today') return d >= today && d <= todayEnd;
  if (dateFilter === 'week') return d >= today && d < weekEnd;
  return true;
}

/**
 * Which lifecycle buckets a top-level filter maps to.
 * `active` = scheduled + in-field-active (matches the prior product intent,
 * now explicitly including en_route/on_site). `all` = everything.
 */
export function bucketsForFilter(filter) {
  switch (filter) {
    case 'active':      return [JOB_BUCKETS.SCHEDULED, JOB_BUCKETS.IN_PROGRESS];
    case 'all':         return [JOB_BUCKETS.UNSCHEDULED, JOB_BUCKETS.SCHEDULED, JOB_BUCKETS.IN_PROGRESS, JOB_BUCKETS.COMPLETED, JOB_BUCKETS.CANCELLED, JOB_BUCKETS.UNKNOWN];
    case 'unscheduled': return [JOB_BUCKETS.UNSCHEDULED];
    case 'scheduled':   return [JOB_BUCKETS.SCHEDULED];
    case 'in_progress': return [JOB_BUCKETS.IN_PROGRESS];
    case 'completed':   return [JOB_BUCKETS.COMPLETED];
    case 'cancelled':   return [JOB_BUCKETS.CANCELLED];
    default:            return [filter];
  }
}

// Service words we won't accept as a "customer name" when derived from a title.
const SERVICE_WORDLIST = /(wash|washing|clean|cleaning|service|pest|control|roof|gutter|window|driveway|walkway|house|premium|recurring|job|visit)/i;

/**
 * Resolve the customer NAME for a job with a safe, consistent fallback chain
 * (mirrors Dispatch/Today: clientName-first). Never throws.
 *
 * Order:
 *   1. task.clientName
 *   2. resolved lead/client display name
 *   3. name derived from task.title — ONLY when the title uses the guaranteed
 *      "Name — Service" em-dash format (from the accepted-quote workflow) and
 *      the left part doesn't look like a service label
 *   4. task.title (as a headline)               → source: 'title'
 *   5. 'Unknown Client'                          → source: 'none'
 *
 * @returns {{ name:string, source:'clientName'|'lead'|'title-derived'|'title'|'none',
 *             isRealName:boolean }}
 */
export function resolveJobCustomer(task, lead) {
  const clientName = typeof task?.clientName === 'string' ? task.clientName.trim() : '';
  if (clientName) return { name: clientName, source: 'clientName', isRealName: true };

  if (lead) {
    const ln = getDisplayName(lead, '');
    if (ln && ln.trim()) return { name: ln.trim(), source: 'lead', isRealName: true };
  }

  const title = typeof task?.title === 'string' ? task.title.trim() : '';
  if (title) {
    // Only the accepted-quote workflow guarantees "Name — Service" (space + em-dash + space).
    if (title.includes(' — ')) {
      const left = title.split(' — ')[0].trim();
      if (left && !SERVICE_WORDLIST.test(left)) {
        return { name: left, source: 'title-derived', isRealName: true };
      }
    }
    // Fall back to the whole title as a headline (not a verified person name).
    return { name: title, source: 'title', isRealName: false };
  }

  return { name: 'Unknown Client', source: 'none', isRealName: false };
}
